import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import CropMapSearchFilter from "./CropMapSearchFilter";
import "./IndiaMap.css";

const normalizeCropName = (name) => {
  if (!name) return "";
  return name
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
};

const IndiaMapImage = ({ 
  isDistrictView = false, 
  isStateView = false,
  selectedDistrict = '', 
  selectedState = '',
  highlightState = '',
  highlightDistrict = '',
  onSelectState
}) => {
  const [mapData, setMapData] = useState(null);
  const [selectedStateLocal, setSelectedStateLocal] = useState(selectedState || null);
  const [hoveredState, setHoveredState] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCrop, setSelectedCrop] = useState(""); // New state for selected crop filter
  const [svgContent, setSvgContent] = useState(null);
  const [currentState, setCurrentState] = useState(selectedState || null);
  const [coordResult, setCoordResult] = useState(null);
  const [showDistrictView, setShowDistrictView] = useState(false);
  const [districtData, setDistrictData] = useState(null);
  const [selectedDistrictDetail, setSelectedDistrictDetail] = useState(null);
  const [showDistrictDetailPopup, setShowDistrictDetailPopup] = useState(false);
  const [districtGeographies, setDistrictGeographies] = useState(null);
  
  // Coordinate input states
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [coordError, setCoordError] = useState('');
  const [coordLoading, setCoordLoading] = useState(false);
  const [showCoordInput, setShowCoordInput] = useState(false);

  // Define geoUrl constants
  const geoUrl = "https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@bcbcba3/geojson/india.geojson";
  const districtGeoUrl = "https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@bcbcba3/geojson/districts.geojson";

  // Function to close district view
  const closeDistrictView = () => {
    setShowDistrictView(false);
    setDistrictData(null);
  };

  // Function to handle coordinate lookup
  const handleCoordinateLookup = async () => {
    setCoordError('');
    setCoordLoading(true);
    
    // Validate coordinates
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    
    if (isNaN(lat) || isNaN(lon)) {
      setCoordError('Please enter valid coordinates');
      setCoordLoading(false);
      return;
    }
    
    if (lat < 6 || lat > 37 || lon < 68 || lon > 98) {
      setCoordError('Coordinates are outside India. Please check your input.');
      setCoordLoading(false);
      return;
    }
    
    try {
      // Use Nominatim reverse geocoding API
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=5&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'AgroFarming-App'
          }
        }
      );
      
      const data = await response.json();
      
      if (data.error) {
        setCoordError('Location not found. Please check coordinates.');
        setCoordLoading(false);
        return;
      }
      
      console.log('Nominatim response:', data); // Debug log
      
      // Extract state name from the response - try multiple fields
      let stateName = data.address?.state || 
                      data.address?.region || 
                      data.address?.state_district ||
                      data.address?.county;
      
      // If still no state, try to extract from display_name
      if (!stateName && data.display_name) {
        const parts = data.display_name.split(',').map(p => p.trim());
        // Usually state is second to last or third to last in display_name
        if (parts.length >= 2) {
          stateName = parts[parts.length - 2]; // Try second to last
        }
      }
      
      if (!stateName) {
        setCoordError(`Could not determine state. API response: ${JSON.stringify(data.address)}`);
        setCoordLoading(false);
        return;
      }
      
      console.log('Extracted state name:', stateName); // Debug log
      
      // Normalize state name to match our data
      // Handle special cases
      const stateMapping = {
        'NCT': 'Delhi',
        'National Capital Territory of Delhi': 'Delhi',
        'Dadra and Nagar Haveli and Daman and Diu': 'Dadra And Nagar Haveli',
        'Andaman and Nicobar Islands': 'Andaman And Nicobar',
        'Jammu and Kashmir': 'Jammu And Kashmir',
        'Arunachal Pradesh': 'Arunachal Pradesh',
        'Himachal Pradesh': 'Himachal Pradesh',
        'Madhya Pradesh': 'Madhya Pradesh',
        'Uttar Pradesh': 'Uttar Pradesh',
        'Andhra Pradesh': 'Andhra Pradesh',
        'West Bengal': 'West Bengal',
        'Tamil Nadu': 'Tamil Nadu'
      };
      
      stateName = stateMapping[stateName] || stateName;
      
      console.log('Mapped state name:', stateName); // Debug log
      console.log('Available states:', mapData ? Object.keys(mapData.stateCrops) : 'No data'); // Debug log
      
      // Check if state exists in our data
      if (mapData && mapData.stateCrops) {
        const matchedState = Object.keys(mapData.stateCrops).find(
          state => state.toLowerCase() === stateName.toLowerCase() ||
                   state.toLowerCase().includes(stateName.toLowerCase()) ||
                   stateName.toLowerCase().includes(state.toLowerCase())
        );
        
        console.log('Matched state:', matchedState); // Debug log
        
        if (matchedState) {
          setSelectedStateLocal(matchedState);
          setCurrentState(matchedState);
          setCoordResult({
            location: data.display_name,
            state: matchedState,
            district: data.address?.county || data.address?.state_district || 'Unknown'
          });
          setCoordError('');
          
          // Call onSelectState if provided
          if (onSelectState) {
            onSelectState(matchedState);
          }
        } else {
          setCoordError(`State "${stateName}" not found in crop database. Available states: ${Object.keys(mapData.stateCrops).slice(0, 5).join(', ')}...`);
        }
      }
    } catch (error) {
      console.error('Error fetching location:', error);
      setCoordError('Failed to fetch location. Please try again.');
    } finally {
      setCoordLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    
    // Fetch crop data
    fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/india-crops`)
      .then((response) => response.json())
      .then((data) => {
        setMapData(data);
      })
      .catch((error) => {
        console.error("Error fetching map data:", error);
      });

    // Fetch India SVG map
    fetch("https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@bcbcba3/geojson/india.geojson")
      .then(response => response.json())
      .then(geojson => {
        // Convert GeoJSON to SVG paths
        const svg = convertGeoJSONToSVG(geojson);
        setSvgContent(svg);
        setIsLoading(false);
      })
      .catch(error => {
        console.error("Error fetching map:", error);
        setIsLoading(false);
      });

    // Fetch district data if in district view
    if (isDistrictView && selectedDistrict) {
      fetch(districtGeoUrl)
        .then(response => response.json())
        .then(geojson => {
          setDistrictGeographies(geojson);
        })
        .catch(error => {
          console.error("Error fetching district data:", error);
        });
    }
  }, [isDistrictView, selectedDistrict]);

  // Auto-select state when in district or state view
  useEffect(() => {
    if ((isDistrictView || isStateView) && selectedState && mapData) {
      setCurrentState(selectedState);
      setSelectedStateLocal(selectedState);
    }
  }, [isDistrictView, isStateView, selectedState, mapData]);

  // Simple GeoJSON to SVG converter
  const convertGeoJSONToSVG = (geojson) => {
    const features = geojson.features;
    const bounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
    
    // Calculate bounds
    features.forEach(feature => {
      const coords = feature.geometry.coordinates;
      traverseCoordinates(coords, (lon, lat) => {
        bounds.minX = Math.min(bounds.minX, lon);
        bounds.maxX = Math.max(bounds.maxX, lon);
        bounds.minY = Math.min(bounds.minY, lat);
        bounds.maxY = Math.max(bounds.maxY, lat);
      });
    });

    const width = 800;
    const height = 900;
    const padding = 20;
    
    const scaleX = (width - 2 * padding) / (bounds.maxX - bounds.minX);
    const scaleY = (height - 2 * padding) / (bounds.maxY - bounds.minY);
    const scale = Math.min(scaleX, scaleY);

    const offsetX = (width - (bounds.maxX - bounds.minX) * scale) / 2;
    const offsetY = (height - (bounds.maxY - bounds.minY) * scale) / 2;

    const project = (lon, lat) => {
      const x = (lon - bounds.minX) * scale + offsetX;
      const y = height - ((lat - bounds.minY) * scale + offsetY);
      return [x, y];
    };

    const paths = features.map(feature => {
      const stateName = feature.properties.st_nm;
      const pathData = [];

      const coords = feature.geometry.coordinates;
      const processPolygon = (polygon) => {
        polygon.forEach((ring, ringIndex) => {
          ring.forEach((point, i) => {
            const [x, y] = project(point[0], point[1]);
            pathData.push(i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`);
          });
          pathData.push('Z');
        });
      };

      if (feature.geometry.type === 'Polygon') {
        processPolygon(coords);
      } else if (feature.geometry.type === 'MultiPolygon') {
        coords.forEach(processPolygon);
      }

      return {
        stateName,
        d: pathData.join(' ')
      };
    });

    return { paths, width, height };
  };

  const traverseCoordinates = (coords, callback) => {
    if (typeof coords[0] === 'number') {
      callback(coords[0], coords[1]);
    } else {
      coords.forEach(coord => traverseCoordinates(coord, callback));
    }
  };

  const getCropIconByName = (cropName) => {
    if (!mapData || !mapData.legend || !mapData.legend.icons) return "🌾";
    const normalized = normalizeCropName(cropName);
    const foundKey = Object.keys(mapData.legend.icons).find(
      (key) => normalizeCropName(key) === normalized
    );
    return foundKey ? mapData.legend.icons[foundKey] : "🌾";
  };

  const getStateColor = (stateName) => {
    // In district or state view, show selected state highlighted and others in light gray
    if ((isDistrictView || isStateView) && selectedState) {
      const normalizedStateName = stateName.toLowerCase().trim();
      const normalizedSelectedState = selectedState.toLowerCase().trim();
      
      // If this is the selected state, show it highlighted
      if (normalizedStateName === normalizedSelectedState || 
          normalizedStateName.includes(normalizedSelectedState) ||
          normalizedSelectedState.includes(normalizedStateName)) {
        return isStateView ? "#3b82f6" : "#FF6B35"; // Blue for state view, Orange for district view
      } else {
        // Show all other states in light gray
        return "#E8E8E8";
      }
    }
    
    // Check if this state should be highlighted (exact match or partial match)
    if (highlightState) {
      const normalizedStateName = stateName.toLowerCase().trim();
      const normalizedHighlightState = highlightState.toLowerCase().trim();
      
      if (normalizedStateName === normalizedHighlightState || 
          normalizedStateName.includes(normalizedHighlightState) ||
          normalizedHighlightState.includes(normalizedStateName)) {
        return "#FF4444"; // Bright red for highlighted state
      }
    }
    
    // PRIORITY 1: Check crop filter first (if selected)
    if (selectedCrop && mapData && mapData.stateCrops && mapData.stateCrops[stateName]) {
      const cropsInState = mapData.stateCrops[stateName].map((crop) =>
        normalizeCropName(crop)
      );
      const normalizedSelectedCrop = normalizeCropName(selectedCrop);
      
      if (!cropsInState.includes(normalizedSelectedCrop)) {
        // State doesn't have this crop - show grey
        return "#E0E0E0";
      }
      
      // State has this crop - show the crop's color
      const cropIndex = mapData.legend?.crops?.findIndex(
        (crop) => normalizeCropName(crop) === normalizedSelectedCrop
      ) ?? -1;
      return cropIndex !== -1 ? mapData.legend.colors[cropIndex] : "#E0E0E0";
    }
    
    // PRIORITY 2: If a state is selected (clicked or from coordinates), highlight only that state
    if (selectedStateLocal || currentState) {
      const activeState = selectedStateLocal || currentState;
      const normalizedStateName = stateName.toLowerCase().trim();
      const normalizedActiveState = activeState.toLowerCase().trim();
      
      if (normalizedStateName === normalizedActiveState || 
          normalizedStateName.includes(normalizedActiveState) ||
          normalizedActiveState.includes(normalizedStateName)) {
        // Show selected state with its crop color
        if (!mapData || !mapData.stateCrops || !mapData.stateCrops[stateName]) {
          return "#4CAF50"; // Default green if no crop data
        }
        
        const primaryCrop = mapData.stateCrops[stateName][0];
        const normalized = normalizeCropName(primaryCrop);
        const cropIndex = mapData.legend?.crops?.findIndex(
          (crop) => normalizeCropName(crop) === normalized
        ) ?? -1;
        return cropIndex !== -1 ? mapData.legend.colors[cropIndex] : "#4CAF50";
      } else {
        // All other states in grey
        return "#D3D3D3";
      }
    }
    
    // PRIORITY 3: Default - show all states with their crop colors (when nothing is selected)
    if (!mapData || !mapData.stateCrops || !mapData.stateCrops[stateName]) {
      return "#E0E0E0";
    }
    
    const primaryCrop = mapData.stateCrops[stateName][0];
    const normalized = normalizeCropName(primaryCrop);
    const cropIndex =
      mapData.legend?.crops?.findIndex(
        (crop) => normalizeCropName(crop) === normalized
      ) ?? -1;
    return cropIndex !== -1 ? mapData.legend.colors[cropIndex] : "#E0E0E0";
  };

  // Function to check if a district should be highlighted
  const getDistrictColor = (districtName, stateName) => {
    if (isDistrictView && selectedDistrict && selectedState) {
      const normalizedDistrictName = districtName.toLowerCase().trim();
      const normalizedSelectedDistrict = selectedDistrict.toLowerCase().trim();
      const normalizedStateName = stateName.toLowerCase().trim();
      const normalizedSelectedState = selectedState.toLowerCase().trim();
      
      // Check if this is the selected district in the selected state
      if ((normalizedDistrictName === normalizedSelectedDistrict || 
           normalizedDistrictName.includes(normalizedSelectedDistrict) ||
           normalizedSelectedDistrict.includes(normalizedDistrictName)) &&
          (normalizedStateName === normalizedSelectedState ||
           normalizedStateName.includes(normalizedSelectedState) ||
           normalizedSelectedState.includes(normalizedStateName))) {
        return "#FF6B35"; // Orange for highlighted district
      }
    }
    return "transparent"; // Transparent for non-highlighted districts
  };

  const handleStateClick = (stateName) => {
    // In state view, don't allow clicking on other states
    if (isStateView && selectedState && stateName !== selectedState) {
      return;
    }
    
    // In district view, don't allow clicking on other states
    if (isDistrictView && selectedState && stateName !== selectedState) {
      return;
    }
    
    const newSelectedState = selectedStateLocal === stateName ? null : stateName;
    setSelectedStateLocal(newSelectedState);
    setCurrentState(newSelectedState);
    
    // Call the onSelectState callback if provided
    if (onSelectState) {
      onSelectState(newSelectedState);
    }
  };

  const handleStateHover = (stateName, event) => {
    setHoveredState(stateName);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleStateLeave = () => {
    setHoveredState(null);
  };

  // Color scheme
  const colors = {
    primary: "#2E7D32",
    secondary: "#4CAF50",
    accent: "#FFC107",
    background: "#F8F9FA",
    card: "#FFFFFF",
    text: "#333333",
    border: "#E0E0E0",
    // highlight: "#FFD700",
  };

  return (
    <div
      className="india-map-wrapper"
      style={{
        marginTop: "30px",
        backgroundColor: colors.background,
        borderRadius: "20px",
        padding: "25px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        textAlign: "center",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          right: "0",
          height: "4px",
          background: `linear-gradient(90deg, ${colors.secondary}, ${colors.accent})`,
          borderRadius: "20px 20px 0 0",
        }}
      />

      <h2
        style={{
          color: colors.primary,
          margin: "0 0 30px",
          fontSize: "28px",
          fontWeight: "700",
          position: "relative",
          display: "inline-block",
        }}
      >
        <span
          style={{
            position: "absolute",
            marginTop: "20px",
            top: "-35px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "40px",
          }}
        >
          <img
            src="https://flagcdn.com/w40/in.png"
            alt="India Flag"
            style={{
              width: "32px",
              height: "24px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        </span>
        <div style={{ marginTop: "30px" }}>
          {(isDistrictView && selectedDistrict) ? 
            `Crop Distribution Map - ${selectedDistrict}, ${selectedState}` : 
            (isStateView && selectedState) ?
            `Crop Distribution Map - ${selectedState} State` :
            "Crop Distribution Map of India"
          }
        </div>
      </h2>

    
            
            

      {isLoading ? (
        <div
          style={{
            padding: "60px 20px",
            backgroundColor: "#F5F5F5",
            borderRadius: "15px",
            border: "2px dashed #BDBDBD",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
          }}
        >
          <div
            style={{
              fontSize: "50px",
              marginBottom: "20px",
              animation: "spin 2s linear infinite",
            }}
          >
            🌾
          </div>
          <div
            style={{ fontSize: "20px", color: "#666", marginBottom: "10px" }}
          >
            Loading Agricultural Data...
          </div>
          <div style={{ fontSize: "16px", color: "#999", maxWidth: "500px" }}>
            Fetching real-time crop distribution from Indian Government API
          </div>
        </div>
      ) : mapData && svgContent ? (
        <>
          <div
            className="india-map-layout"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "1200px",
              margin: "0 auto",
              display: "flex",
              justifyContent: "space-between",
              gap: "30px",
            }}
          >
            <div
              className="india-map-canvas"
              style={{
                flex: "0 0 70%",
                position: "relative",
                height: "700px",
                background: "linear-gradient(135deg, #a8e6cf 0%, #dcedc1 100%)",
                borderRadius: "12px",
                border: `2px solid ${isDistrictView ? "#FF6B35" : colors.primary}`,
                overflow: "hidden",
                boxShadow: isDistrictView ? "0 6px 15px rgba(255,107,53,0.2)" : "0 6px 15px rgba(0,0,0,0.1)",
              }}
            >
              {/* District indicator banner */}
              {isDistrictView && selectedDistrict && (
                <div style={{
                  position: "absolute",
                  top: "10px",
                  left: "10px",
                  backgroundColor: "rgba(255, 107, 53, 0.9)",
                  color: "white",
                  padding: "8px 15px",
                  borderRadius: "20px",
                  fontSize: "14px",
                  fontWeight: "600",
                  zIndex: 1000,
                  boxShadow: "0 3px 10px rgba(255,107,53,0.3)",
                  border: "2px solid #FF6B35"
                }}>
                </div>
              )}

              {/* Crop filter dropdown inside map container */}
              {mapData && mapData.legend && mapData.legend.crops && (
                <div style={{
                  position: "absolute",
                  top: "10px",
                  left: "10px",
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  zIndex: 1000
                }}>
                  {/* Search Filter */}
                  <CropMapSearchFilter
                    statesData={mapData.stateCrops}
                    onStateSelect={(stateName) => {
                      setSelectedStateLocal(stateName);
                      setCurrentState(stateName);
                      if (onSelectState) {
                        onSelectState(stateName);
                      }
                    }}
                    onDistrictSelect={(district) => {
                      // Show district detail popup
                      setSelectedDistrictDetail(district);
                      setShowDistrictDetailPopup(true);
                    }}
                    onReset={() => {
                      setSelectedStateLocal(null);
                      setCurrentState(null);
                      setSelectedCrop("");
                      if (onSelectState) {
                        onSelectState(null);
                      }
                    }}
                  />
                </div>
              )}
              
              {/* Crop Dropdown - Right Side */}
              {mapData && mapData.legend && mapData.legend.crops && (
                <select
                  value={selectedCrop}
                  onChange={(e) => {
                    const newCrop = e.target.value;
                    setSelectedCrop(newCrop);
                    
                    // If "All Crops" is selected, clear the state selection to show all states
                    if (newCrop === "") {
                      setSelectedStateLocal(null);
                      setCurrentState(null);
                      if (onSelectState) {
                        onSelectState(null);
                      }
                    }
                  }}
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    padding: "8px",
                    borderRadius: "8px",
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.card,
                    fontSize: "14px",
                    zIndex: 1000,
                    cursor: "pointer",
                  }}
                >
                  <option value="">All Crops</option>
                  {mapData.legend.crops.map((crop) => (
                    <option key={crop} value={crop}>
                      {crop}
                    </option>
                  ))}
                </select>
              )}

              {/* Coordinate Search Box - Bottom Right */}
              <div style={{
                position: "absolute",
                bottom: "15px",
                right: "15px",
                zIndex: 1000,
              }}>
                {!showCoordInput ? (
                  <button
                    onClick={() => setShowCoordInput(true)}
                    style={{
                      padding: "10px 15px",
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      color: colors.primary,
                      border: `2px solid ${colors.primary}`,
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
                      transition: "all 0.3s ease"
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primary;
                      e.currentTarget.style.color = "white";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
                      e.currentTarget.style.color = colors.primary;
                    }}
                  >
                    📍 Search by Coordinates
                  </button>
                ) : (
                  <AnimatePresence>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.98)",
                        borderRadius: "12px",
                        padding: "15px",
                        boxShadow: "0 5px 20px rgba(0,0,0,0.3)",
                        border: `2px solid ${colors.primary}`,
                        minWidth: "320px",
                        maxWidth: "350px"
                      }}
                    >
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "12px"
                      }}>
                        <h4 style={{
                          margin: 0,
                          fontSize: "14px",
                          fontWeight: "600",
                          color: colors.primary
                        }}>
                          📍 Find State by Coordinates
                        </h4>
                        <button
                          onClick={() => {
                            setShowCoordInput(false);
                            setCoordError('');
                            setCoordResult(null);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            fontSize: "18px",
                            cursor: "pointer",
                            color: "#999",
                            padding: "0",
                            lineHeight: "1"
                          }}
                        >
                          ×
                        </button>
                      </div>

                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px"
                      }}>
                        <div>
                          <input
                            type="number"
                            step="any"
                            placeholder="Latitude (e.g., 28.5494)"
                            value={latitude}
                            onChange={(e) => setLatitude(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px",
                              border: `1px solid ${colors.border}`,
                              borderRadius: "6px",
                              fontSize: "13px",
                              boxSizing: "border-box"
                            }}
                          />
                        </div>

                        <div>
                          <input
                            type="number"
                            step="any"
                            placeholder="Longitude (e.g., 77.1578)"
                            value={longitude}
                            onChange={(e) => setLongitude(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px",
                              border: `1px solid ${colors.border}`,
                              borderRadius: "6px",
                              fontSize: "13px",
                              boxSizing: "border-box"
                            }}
                          />
                        </div>

                        <button
                          onClick={handleCoordinateLookup}
                          disabled={coordLoading}
                          style={{
                            width: "100%",
                            padding: "10px",
                            backgroundColor: coordLoading ? "#ccc" : colors.primary,
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: coordLoading ? "not-allowed" : "pointer",
                            fontSize: "13px",
                            fontWeight: "600"
                          }}
                        >
                          {coordLoading ? "🔍 Searching..." : "🔍 Search"}
                        </button>
                       

                        {coordError && (
                          <div style={{
                            padding: "8px",
                            backgroundColor: "#ffebee",
                            border: "1px solid #ef5350",
                            borderRadius: "6px",
                            color: "#c62828",
                            fontSize: "11px",
                            marginTop: "5px"
                          }}>
                            ⚠️ {coordError}
                          </div>
                        )}


                      </div>
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>

              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 900, center: [78.9629, 23] }}
                style={{ width: "100%", height: "100%" }}
              >
                {/* Render states */}
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const stateName = geo.properties.st_nm;
                      const color = getStateColor(stateName);
                      
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={color}
                          stroke={(isDistrictView || isStateView) && selectedState && stateName === selectedState ? (isStateView ? "#1d4ed8" : "#FF6B35") : "#333"}
                          strokeWidth={(isDistrictView || isStateView) && selectedState && stateName === selectedState ? 2.5 : 1}
                          onMouseEnter={(event) =>
                            handleStateHover(stateName, event)
                          }
                          onMouseLeave={handleStateLeave}
                          onClick={() => handleStateClick(stateName)}
                          style={{
                            default: {
                              outline: "none",
                              transition: "all 0.3s ease",
                            },
                            hover: {
                              fill: (isDistrictView || isStateView) && selectedState && stateName === selectedState ? (isStateView ? "#60a5fa" : "#FF8C42") : colors.highlight,
                              outline: "none",
                              stroke: "#333",
                              strokeWidth: 2,
                              filter:
                                "drop-shadow(0 0 8px rgba(255,215,0,0.5))",
                            },
                            pressed: {
                              fill: colors.accent,
                              outline: "none",
                              stroke: "#333",
                              strokeWidth: 2,
                            },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>

                {/* Render district overlay if in district view */}
                {isDistrictView && districtGeographies && (
                  <Geographies geography={districtGeographies}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const districtName = geo.properties.district || geo.properties.DISTRICT || geo.properties.name;
                        const stateName = geo.properties.state || geo.properties.STATE || geo.properties.st_nm;
                        const districtColor = getDistrictColor(districtName, stateName);
                        
                        // Only render if this district should be highlighted
                        if (districtColor === "transparent") return null;
                        
                        return (
                          <Geography
                            key={`district-${geo.rsmKey}`}
                            geography={geo}
                            fill={districtColor}
                            stroke="#FF6B35"
                            strokeWidth={3}
                            style={{
                              default: {
                                outline: "none",
                                transition: "all 0.3s ease",
                                filter: "drop-shadow(0 0 10px rgba(255,107,53,0.7))",
                              },
                              hover: {
                                fill: "#FF8C42",
                                outline: "none",
                                stroke: "#FF6B35",
                                strokeWidth: 4,
                                filter: "drop-shadow(0 0 15px rgba(255,107,53,0.9))",
                              },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>
                )}

                {/* Fallback: Add a visual indicator for the selected district */}
       
              </ComposableMap>

              {hoveredState && mapData.stateCrops?.[hoveredState] && (
                <div
                  style={{
                    position: "fixed",
                    top: `${tooltipPosition.y + 15}px`,
                    left: `${tooltipPosition.x + 15}px`,
                    backgroundColor: "rgba(0,0,0,0.85)",
                    color: "white",
                    padding: "15px",
                    borderRadius: "10px",
                    fontSize: "14px",
                    pointerEvents: "none",
                    zIndex: 1000,
                    maxWidth: "250px",
                    backdropFilter: "blur(4px)",
                    boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      style={{
                        width: "10px",
                        height: "10px",
                        backgroundColor: getStateColor(hoveredState),
                        borderRadius: "50%",
                        marginRight: "10px",
                      }}
                    />
                    <strong style={{ fontSize: "16px" }}>{hoveredState}</strong>
                  </div>
                  {isDistrictView && selectedDistrict && hoveredState === selectedState && (
                    <div style={{ marginTop: "8px", fontSize: "12px", color: "#FFD700" }}>
                      📍 Selected District: {selectedDistrict}
                    </div>
                  )}
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#ccc" }}>
                    Click to view district details
                  </div>
                </div>
              )}
            </div>

            <div
              className="india-map-sidebar"
              style={{
                flex: "0 0 25%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "700px",
              }}
            >
              <AnimatePresence>
                {((currentState || selectedStateLocal) && mapData?.stateCrops?.[currentState || selectedStateLocal]) || 
                 ((isDistrictView || isStateView) && selectedState && mapData?.stateCrops?.[selectedState]) ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    style={{
                      backgroundColor: isDistrictView ? "rgba(255, 107, 53, 0.1)" : "rgba(232, 245, 232, 0.95)",
                      borderRadius: "15px",
                      border: `2px solid ${isDistrictView ? "#FF6B35" : colors.secondary}`,
                      padding: "20px",
                      textAlign: "justify",
                      boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                      backdropFilter: "blur(4px)",
                      position: "relative",
                      height: "58%",
                    }}
                  >
                    <h3
                      style={{
                        color: isDistrictView ? "#FF6B35" : colors.primary,
                        marginBottom: "15px",
                        fontSize: "18px",
                        display: "flex",
                        alignItems: "center",
                        fontWeight: "600",
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: isDistrictView ? "#FF6B35" : colors.secondary,
                          color: "white",
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: "10px",
                        }}
                      >
                        {isDistrictView ? "🏛️" : "📍"}
                      </span>
                      {(isDistrictView || isStateView) ? 
                        `${selectedState}${isDistrictView ? ` - ${selectedDistrict} District` : ' State'} - Crop Details` : 
                        `${selectedStateLocal || currentState} - Crop Details`
                      }
                    </h3>
                    
                    {(isDistrictView || isStateView) && selectedDistrict && (
                      <div style={{
                        marginBottom: "15px",
                        padding: "10px",
                        backgroundColor: isDistrictView ? "rgba(255, 107, 53, 0.1)" : "rgba(59, 130, 246, 0.1)",
                        borderRadius: "8px",
                        border: isDistrictView ? "1px solid #FF6B35" : "1px solid #3b82f6",
                        fontSize: "14px",
                        color: isDistrictView ? "#D2691E" : "#1d4ed8"
                      }}>
                        <strong>📍 Currently viewing:</strong> {isDistrictView ? `${selectedDistrict} District` : `${selectedState} State`}
                      </div>
                    )}
                    
                    <div>
                      <div
                        style={{
                          marginBottom: "12px",
                          padding: "12px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          maxWidth: "100%",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            color: (isDistrictView || isStateView) ? (isDistrictView ? "#FF6B35" : "#3b82f6") : colors.primary,
                            marginBottom: "5px",
                          }}
                        >
                          Primary Crop
                        </div>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span
                            style={{
                              fontSize: "24px",
                              marginRight: "10px",
                              width: "40px",
                              height: "40px",
                              backgroundColor: isDistrictView ? "rgba(255, 107, 53, 0.1)" : "rgba(46, 125, 50, 0.1)",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {getCropIconByName(
                              mapData.stateCrops[selectedState || selectedStateLocal || currentState][0]
                            )}
                          </span>
                          <div>
                            <div
                              style={{
                                fontWeight: "500",
                                wordWrap: "break-word",
                                overflowWrap: "break-word",
                                maxWidth: "100%",
                              }}
                            >
                              {mapData.stateCrops[selectedState || selectedStateLocal || currentState][0]}
                            </div>
                            {mapData.cropProduction?.[
                              mapData.stateCrops[selectedState || selectedStateLocal || currentState][0]
                            ] && (
                              <div
                                style={{
                                  color: isDistrictView ? "#FF6B35" : colors.primary,
                                  fontWeight: "600",
                                }}
                              >
                                {mapData.cropProduction[
                                  mapData.stateCrops[selectedState || selectedStateLocal || currentState][0]
                                ].toFixed(1)}{" "}
                                MT
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {mapData.stateCrops[selectedState || selectedStateLocal || currentState][1] && (
                        <div
                          style={{
                            padding: "12px",
                            backgroundColor: "white",
                            borderRadius: "8px",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            maxWidth: "100%",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              color: isDistrictView ? "#FF6B35" : colors.primary,
                              marginBottom: "5px",
                            }}
                          >
                            Secondary Crop
                          </div>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <span
                              style={{
                                fontSize: "24px",
                                marginRight: "10px",
                                width: "40px",
                                height: "40px",
                                backgroundColor: (isDistrictView || isStateView) ? 
                                  (isDistrictView ? "rgba(255, 107, 53, 0.1)" : "rgba(59, 130, 246, 0.1)") : 
                                  "rgba(46, 125, 50, 0.1)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {getCropIconByName(
                                mapData.stateCrops[selectedState || selectedStateLocal || currentState][1]
                              )}
                            </span>
                            <div>
                              <div
                                style={{
                                  fontWeight: "500",
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                  maxWidth: "100%",
                                }}
                              >
                                {mapData.stateCrops[selectedState || selectedStateLocal || currentState][1]}
                              </div>
                              {mapData.cropProduction?.[
                                mapData.stateCrops[selectedState || selectedStateLocal || currentState][1]
                              ] && (
                                <div
                                  style={{
                                    color: (isDistrictView || isStateView) ? 
                                      (isDistrictView ? "#FF6B35" : "#3b82f6") : colors.primary,
                                    fontWeight: "600",
                                  }}
                                >
                                  {mapData.cropProduction[
                                    mapData.stateCrops[selectedState || selectedStateLocal || currentState][1]
                                  ].toFixed(1)}{" "}
                                  MT
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* View District Details Button */}
                    {((currentState || selectedStateLocal) || ((isStateView || isDistrictView) && selectedState)) && !isDistrictView && (
                      <div style={{ marginTop: "15px" }}>
                        <button
                          onClick={() => {
                            const stateToShow = selectedState || currentState || selectedStateLocal;
                            // Fetch district data for the selected state
                            fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/districts/${encodeURIComponent(stateToShow)}`)
                              .then(response => response.json())
                              .then(data => {
                                setDistrictData(data);
                                setShowDistrictView(true);
                              })
                              .catch(error => {
                                console.error("Error fetching district data:", error);
                                // Show fallback district data
                                setDistrictData({
                                  state: stateToShow,
                                  totalDistricts: 5,
                                  dataSource: "Government Agricultural Database",
                                  districts: [
                                    {
                                      district: `${stateToShow} Central District`,
                                      primary: mapData?.stateCrops?.[stateToShow]?.[0] || "Rice",
                                      secondary: mapData?.stateCrops?.[stateToShow]?.[1] || "Wheat",
                                      primaryProd: 150.5,
                                      secondaryProd: 89.2
                                    },
                                    {
                                      district: `${stateToShow} Northern District`,
                                      primary: mapData?.stateCrops?.[stateToShow]?.[0] || "Rice",
                                      secondary: mapData?.stateCrops?.[stateToShow]?.[1] || "Wheat",
                                      primaryProd: 142.8,
                                      secondaryProd: 76.4
                                    },
                                    {
                                      district: `${stateToShow} Southern District`,
                                      primary: mapData?.stateCrops?.[stateToShow]?.[1] || "Wheat",
                                      secondary: mapData?.stateCrops?.[stateToShow]?.[0] || "Rice",
                                      primaryProd: 128.3,
                                      secondaryProd: 95.7
                                    },
                                    {
                                      district: `${stateToShow} Eastern District`,
                                      primary: mapData?.stateCrops?.[stateToShow]?.[0] || "Rice",
                                      secondary: "Sugarcane",
                                      primaryProd: 167.2,
                                      secondaryProd: 112.8
                                    },
                                    {
                                      district: `${stateToShow} Western District`,
                                      primary: mapData?.stateCrops?.[stateToShow]?.[1] || "Wheat",
                                      secondary: "Cotton",
                                      primaryProd: 134.6,
                                      secondaryProd: 68.9
                                    }
                                  ]
                                });
                                setShowDistrictView(true);
                              });
                          }}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            backgroundColor: colors.primary,
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            transition: "all 0.3s ease",
                            boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = colors.secondary;
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "0 5px 15px rgba(0,0,0,0.3)";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = colors.primary;
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 3px 8px rgba(0,0,0,0.2)";
                          }}
                        >
                          <span>🏛️</span>
                          View District Details
                        </button>
                      </div>
                    )}
                  </motion.div>
                ) : null}
              </AnimatePresence>

              <div
                style={{
                  backgroundColor: colors.card,
                  borderRadius: "15px",
                  border: `1px solid ${colors.border}`,
                  padding: "20px",
                  textAlign: "left",
                  boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                  height: "38%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <h3
                  style={{
                    color: colors.primary,
                    marginBottom: "15px",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  🌾 Crop Distribution Legend
                </h3>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: "8px",
                    overflowY: "auto",
                    maxHeight: "calc(100% - 40px)",
                  }}
                >
                  {mapData.legend?.crops?.slice(0, 8).map((crop, index) => (
                    <div
                      key={crop}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "6px",
                        backgroundColor: "white",
                        borderRadius: "6px",
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      <div
                        style={{
                          width: "14px",
                          height: "14px",
                          backgroundColor: mapData.legend.colors[index],
                          marginRight: "8px",
                          borderRadius: "3px",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: "12px", fontWeight: "500" }}>
                        {getCropIconByName(crop)} {crop}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Section */}
          <div
            style={{
              marginTop: "30px",
              textAlign: "left",
              padding: "20px",
              backgroundColor: isDistrictView ? "#FFF3E0" : "#FFF8E1",
              borderRadius: "15px",
              border: `1px solid ${isDistrictView ? "#FF6B35" : colors.accent}`,
              boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
            }}
          >
            <h3
              style={{
                color: isDistrictView ? "#FF6B35" : "#F57F17",
                marginBottom: "20px",
                fontSize: "20px",
                display: "flex",
                alignItems: "center",
                fontWeight: "600",
              }}
            >
              <span
                style={{
                  backgroundColor: isDistrictView ? "rgba(255, 107, 53, 0.2)" : "rgba(255, 193, 7, 0.2)",
                  color: isDistrictView ? "#FF6B35" : "#F57F17",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "10px",
                }}
              >
                📊
              </span>
              {isDistrictView && selectedDistrict ? 
                `Crop Production Statistics - ${selectedDistrict} District (2024)` : 
                (isStateView && selectedState) ?
                `Crop Production Statistics - ${selectedState} State (2024)` :
                (currentState || selectedStateLocal) ?
                `Crop Production Statistics - ${currentState || selectedStateLocal} State (2024)` :
                "Crop Production Statistics of India (2024)"
              }
            </h3>
            
            {isDistrictView && selectedDistrict && selectedState ? (
              // District-specific statistics
              <div>
                {mapData?.stateCrops?.[selectedState] ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: "20px",
                    }}
                  >
                    {/* Primary Crop Card */}
                    <div
                      style={{
                        padding: "20px",
                        backgroundColor: "white",
                        borderRadius: "12px",
                        border: "2px solid #FF6B35",
                        boxShadow: "0 4px 12px rgba(255,107,53,0.1)",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                        <span
                          style={{
                            fontSize: "32px",
                            marginRight: "15px",
                            backgroundColor: "rgba(255, 107, 53, 0.1)",
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {getCropIconByName(mapData.stateCrops[selectedState][0])}
                        </span>
                        <div>
                          <div style={{ fontSize: "12px", color: "#FF6B35", fontWeight: "600", marginBottom: "5px" }}>
                            PRIMARY CROP
                          </div>
                          <div style={{ fontWeight: "700", color: "#333", fontSize: "18px" }}>
                            {mapData.stateCrops[selectedState][0]}
                          </div>
                          <div style={{ color: "#666", fontSize: "14px" }}>
                            Main crop in {selectedDistrict}
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          position: "absolute",
                          bottom: "0",
                          left: "0",
                          height: "4px",
                          width: "100%",
                          background: "linear-gradient(90deg, #FF6B35, #FF8C42)",
                          borderRadius: "0 0 12px 12px",
                        }}
                      />
                    </div>

                    {/* Secondary Crop Card (if exists) */}
                    {mapData.stateCrops[selectedState][1] && (
                      <div
                        style={{
                          padding: "20px",
                          backgroundColor: "white",
                          borderRadius: "12px",
                          border: "2px solid #FFB74D",
                          boxShadow: "0 4px 12px rgba(255,183,77,0.1)",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                          <span
                            style={{
                              fontSize: "32px",
                              marginRight: "15px",
                              backgroundColor: "rgba(255, 183, 77, 0.1)",
                              width: "60px",
                              height: "60px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {getCropIconByName(mapData.stateCrops[selectedState][1])}
                          </span>
                          <div>
                            <div style={{ fontSize: "12px", color: "#FFB74D", fontWeight: "600", marginBottom: "5px" }}>
                              SECONDARY CROP
                            </div>
                            <div style={{ fontWeight: "700", color: "#333", fontSize: "18px" }}>
                              {mapData.stateCrops[selectedState][1]}
                            </div>
                            <div style={{ color: "#666", fontSize: "14px" }}>
                              Secondary crop in {selectedDistrict}
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            bottom: "0",
                            left: "0",
                            height: "4px",
                            width: "80%",
                            background: "linear-gradient(90deg, #FFB74D, #FFCC80)",
                            borderRadius: "0 0 12px 12px",
                          }}
                        />
                      </div>
                    )}

                    {/* District Info Card */}
                    <div
                      style={{
                        padding: "20px",
                        backgroundColor: "white",
                        borderRadius: "12px",
                        border: "2px solid #81C784",
                        boxShadow: "0 4px 12px rgba(129,199,132,0.1)",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                        <span
                          style={{
                            fontSize: "32px",
                            marginRight: "15px",
                            backgroundColor: "rgba(129, 199, 132, 0.1)",
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          🏛️
                        </span>
                        <div>
                          <div style={{ fontSize: "12px", color: "#81C784", fontWeight: "600", marginBottom: "5px" }}>
                            DISTRICT INFO
                          </div>
                          <div style={{ fontWeight: "700", color: "#333", fontSize: "18px" }}>
                            {selectedDistrict}
                          </div>
                          <div style={{ color: "#666", fontSize: "14px" }}>
                            Located in {selectedState}
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          position: "absolute",
                          bottom: "0",
                          left: "0",
                          height: "4px",
                          width: "60%",
                          background: "linear-gradient(90deg, #81C784, #A5D6A7)",
                          borderRadius: "0 0 12px 12px",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                    <div style={{ fontSize: "48px", marginBottom: "15px" }}>📊</div>
                    <div>No crop data available for {selectedDistrict} district</div>
                  </div>
                )}
              </div>
            ) : (
              // State-specific or India-wide statistics
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "20px",
                }}
              >
                {/* Show state-specific crops if a state is selected */}
                {(currentState || selectedStateLocal || (isStateView && selectedState)) && mapData?.stateCrops ? (
                  // State-specific crop display
                  (() => {
                    const stateToShow = currentState || selectedStateLocal || selectedState;
                    const stateCrops = mapData.stateCrops[stateToShow];
                    
                    if (!stateCrops || stateCrops.length === 0) {
                      return (
                        <div style={{ 
                          gridColumn: "1 / -1", 
                          textAlign: "center", 
                          padding: "40px", 
                          color: "#999" 
                        }}>
                          <div style={{ fontSize: "48px", marginBottom: "15px" }}>📊</div>
                          <div>No crop data available for {stateToShow}</div>
                        </div>
                      );
                    }
                    
                    return stateCrops.slice(0, 6).map((crop, index) => {
                      const production = mapData.cropProduction?.[crop] || 0;
                      const isMainCrop = index === 0;
                      
                      return (
                        <div
                          key={crop}
                          style={{
                            padding: "15px",
                            backgroundColor: "white",
                            borderRadius: "10px",
                            border: isMainCrop ? "2px solid #4CAF50" : `1px solid ${colors.border}`,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            boxShadow: isMainCrop ? "0 4px 12px rgba(76,175,80,0.2)" : "0 3px 8px rgba(0,0,0,0.05)",
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span
                              style={{
                                fontSize: "24px",
                                marginRight: "15px",
                                backgroundColor: isMainCrop ? "rgba(76, 175, 80, 0.1)" : "rgba(255, 193, 7, 0.1)",
                                width: "50px",
                                height: "50px",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {getCropIconByName(crop)}
                            </span>
                            <div>
                              <div style={{ 
                                fontWeight: "600", 
                                color: colors.text,
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                              }}>
                                {crop}
                                {isMainCrop && (
                                  <span style={{
                                    fontSize: "10px",
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "2px 6px",
                                    borderRadius: "10px",
                                    fontWeight: "600"
                                  }}>
                                    PRIMARY
                                  </span>
                                )}
                              </div>
                              <div style={{
                                color: isMainCrop ? "#4CAF50" : "#FF6F00",
                                fontWeight: "600",
                                fontSize: "16px",
                              }}>
                                {production > 0 ? `${production.toFixed(1)} MT` : "Production data N/A"}
                              </div>
                              <div style={{ 
                                fontSize: "12px", 
                                color: "#666",
                                marginTop: "2px"
                              }}>
                                Grown in {stateToShow}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              position: "absolute",
                              bottom: "0",
                              left: "0",
                              height: "4px",
                              width: `${Math.min(100, ((index === 0 ? 100 : 80 - index * 10)))}%`,
                              background: isMainCrop 
                                ? "linear-gradient(90deg, #4CAF50, #66BB6A)" 
                                : `linear-gradient(90deg, ${colors.accent}, #FF9800)`,
                              borderRadius: "0 0 0 10px",
                            }}
                          />
                        </div>
                      );
                    });
                  })()
                ) : (
                  // India-wide statistics (when no state is selected)
                  mapData.cropProduction &&
                  Object.entries(mapData.cropProduction)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 8) // Show top 8 crops for India
                    .map(([crop, production]) => (
                      <div
                        key={crop}
                        style={{
                          padding: "15px",
                          backgroundColor: "white",
                          borderRadius: "10px",
                          border: `1px solid ${colors.border}`,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          boxShadow: "0 3px 8px rgba(0,0,0,0.05)",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span
                            style={{
                              fontSize: "24px",
                              marginRight: "15px",
                              backgroundColor: "rgba(255, 193, 7, 0.1)",
                              width: "50px",
                              height: "50px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {getCropIconByName(crop)}
                          </span>
                          <div>
                            <div
                              style={{ fontWeight: "600", color: colors.text }}
                            >
                              {crop}
                            </div>
                            <div
                              style={{
                                color: "#FF6F00",
                                fontWeight: "600",
                                fontSize: "18px",
                              }}
                            >
                              {production.toFixed(1)} MT
                            </div>
                            <div style={{ 
                              fontSize: "12px", 
                              color: "#666",
                              marginTop: "2px"
                            }}>
                              All India Production
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            bottom: "0",
                            left: "0",
                            height: "4px",
                            width: `${Math.min(100, (production / 300) * 100)}%`,
                            background: `linear-gradient(90deg, ${colors.accent}, #FF9800)`,
                            borderRadius: "0 0 0 10px",
                          }}
                        />
                      </div>
                    ))
                )}
              </div>
            )}
          </div>

          {/* District View Modal */}
          <AnimatePresence>
            {showDistrictView && districtData && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.7)",
                  zIndex: 2000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "20px",
                }}
                onClick={closeDistrictView}
              >
                <motion.div
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                  style={{
                    backgroundColor: "white",
                    borderRadius: "20px",
                    padding: "30px",
                    maxWidth: "900px",
                    width: "100%",
                    maxHeight: "80vh",
                    overflowY: "auto",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px" }}>
                    <h2 style={{ color: colors.primary, margin: 0, fontSize: "26px", fontWeight: "700" }}>
                      📍 {districtData.state} - District Crop Distribution
                    </h2>
                    <button
                      onClick={closeDistrictView}
                      style={{
                        backgroundColor: "transparent",
                        border: "none",
                        fontSize: "28px",
                        cursor: "pointer",
                        color: "#999",
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  <div style={{ marginBottom: "20px", padding: "15px", backgroundColor: "#E8F5E9", borderRadius: "10px" }}>
                    <div style={{ fontSize: "14px", color: "#666" }}>
                      Total Districts: <strong>{districtData.totalDistricts}</strong> | 
                      Data Source: <strong>{districtData.dataSource}</strong>
                    </div>
                  </div>

                  {districtData.districts.length > 0 ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: "20px",
                      }}
                    >
                      {districtData.districts.map((district, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          style={{
                            backgroundColor: "#F8F9FA",
                            borderRadius: "12px",
                            padding: "20px",
                            border: "2px solid #E0E0E0",
                            transition: "all 0.3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = colors.secondary;
                            e.currentTarget.style.boxShadow = "0 5px 15px rgba(0,0,0,0.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "#E0E0E0";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          <h4 style={{ color: colors.primary, marginBottom: "15px", fontSize: "16px", fontWeight: "600" }}>
                            {district.district}
                          </h4>

                          <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "white", borderRadius: "8px" }}>
                            <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px", fontWeight: "600" }}>
                              PRIMARY CROP
                            </div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <span style={{ fontSize: "20px", marginRight: "8px" }}>
                                  {getCropIconByName(district.primary)}
                                </span>
                                <span style={{ fontWeight: "600", color: colors.text }}>
                                  {district.primary}
                                </span>
                              </div>
                              {district.primaryProd && (
                                <span style={{ fontSize: "12px", color: colors.primary, fontWeight: "600" }}>
                                  {district.primaryProd.toFixed(1)} MT
                                </span>
                              )}
                            </div>
                          </div>

                          {district.secondary && (
                            <div style={{ padding: "12px", backgroundColor: "white", borderRadius: "8px" }}>
                              <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px", fontWeight: "600" }}>
                                SECONDARY CROP
                              </div>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <span style={{ fontSize: "20px", marginRight: "8px" }}>
                                    {getCropIconByName(district.secondary)}
                                  </span>
                                  <span style={{ fontWeight: "600", color: colors.text }}>
                                    {district.secondary}
                                  </span>
                                </div>
                                {district.secondaryProd && (
                                  <span style={{ fontSize: "12px", color: colors.primary, fontWeight: "600" }}>
                                    {district.secondaryProd.toFixed(1)} MT
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                      <div style={{ fontSize: "48px", marginBottom: "15px" }}>📭</div>
                      <div>No district data available for this state</div>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* District Detail Popup (from search) */}
          <AnimatePresence>
            {showDistrictDetailPopup && selectedDistrictDetail && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.7)",
                  zIndex: 2000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "20px",
                }}
                onClick={() => setShowDistrictDetailPopup(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                  style={{
                    backgroundColor: "white",
                    borderRadius: "20px",
                    padding: "30px",
                    maxWidth: "500px",
                    width: "100%",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px" }}>
                    <h2 style={{ color: colors.primary, margin: 0, fontSize: "24px", fontWeight: "700" }}>
                      🏛️ {selectedDistrictDetail.name}
                    </h2>
                    <button
                      onClick={() => setShowDistrictDetailPopup(false)}
                      style={{
                        backgroundColor: "transparent",
                        border: "none",
                        fontSize: "28px",
                        cursor: "pointer",
                        color: "#999",
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  <div style={{ marginBottom: "20px", padding: "15px", backgroundColor: "#E8F5E9", borderRadius: "10px" }}>
                    <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>
                      <strong>State:</strong> {selectedDistrictDetail.stateName}
                    </div>
                    {selectedDistrictDetail.primaryProd && (
                      <div style={{ fontSize: "14px", color: "#666" }}>
                        <strong>Total Production:</strong> {(selectedDistrictDetail.primaryProd + (selectedDistrictDetail.secondaryProd || 0)).toFixed(1)} MT
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                    {/* Primary Crop */}
                    <div style={{ 
                      padding: "20px", 
                      backgroundColor: "#F8F9FA", 
                      borderRadius: "12px",
                      border: "2px solid #4CAF50"
                    }}>
                      <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px", fontWeight: "600" }}>
                        PRIMARY CROP
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span style={{ fontSize: "32px", marginRight: "15px" }}>
                            {getCropIconByName(selectedDistrictDetail.primaryCrop)}
                          </span>
                          <div>
                            <div style={{ fontWeight: "700", fontSize: "18px", color: colors.text }}>
                              {selectedDistrictDetail.primaryCrop}
                            </div>
                            {selectedDistrictDetail.primaryProd && (
                              <div style={{ fontSize: "14px", color: colors.primary, fontWeight: "600" }}>
                                {selectedDistrictDetail.primaryProd.toFixed(1)} MT
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Secondary Crop */}
                    {selectedDistrictDetail.secondaryCrop && (
                      <div style={{ 
                        padding: "20px", 
                        backgroundColor: "#F8F9FA", 
                        borderRadius: "12px",
                        border: "2px solid #FF9800"
                      }}>
                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px", fontWeight: "600" }}>
                          SECONDARY CROP
                        </div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ fontSize: "32px", marginRight: "15px" }}>
                              {getCropIconByName(selectedDistrictDetail.secondaryCrop)}
                            </span>
                            <div>
                              <div style={{ fontWeight: "700", fontSize: "18px", color: colors.text }}>
                                {selectedDistrictDetail.secondaryCrop}
                              </div>
                              {selectedDistrictDetail.secondaryProd && (
                                <div style={{ fontSize: "14px", color: "#FF6F00", fontWeight: "600" }}>
                                  {selectedDistrictDetail.secondaryProd.toFixed(1)} MT
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: "20px", textAlign: "center" }}>
                    <button
                      onClick={() => setShowDistrictDetailPopup(false)}
                      style={{
                        padding: "12px 30px",
                        backgroundColor: colors.primary,
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Close
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      ) : (
        <div
          style={{
            padding: "40px",
            backgroundColor: "#FFEBEE",
            borderRadius: "15px",
            border: "2px dashed #F44336",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "24px", color: "#F44336", marginBottom: "15px" }}>
            ❌ Data Loading Failed
          </div>
          <div style={{ fontSize: "16px", color: "#E57373" }}>
            Please check your connection and try again
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default IndiaMapImage;